/**
 * Fix Guide PDF — CORE builders (BIZ Arc 3, ADR-059 D7/D9 as amended
 * 12 June 2026).
 *
 * TWO templates for two audiences:
 *
 *   buildFixGuidePdfCore — the OWNER report (the portal's Report PDF modal).
 *     Page 1 ALWAYS summarises the full open set for the section — filters
 *     and selection change the card pages, never the summary (mirrors the
 *     Fix Guide Summary-stats rule). Priority bar is High → Medium → Low
 *     with all three labelled, zeros shown. A Fix Cards by Assignee block
 *     replaces the category-score spread (D9 amendment).
 *
 *   buildSupplierWorkPdfCore — the SUPPLIER attachment (invitation email).
 *     Work-only: brand header "on behalf of <business>", the invited cards
 *     (ref / title / detected / to-do / priority), the bundle-reply
 *     instruction, the expiry. NO grades, scores, category bars,
 *     potential-gain points, or assignee information (D7 amendment — the
 *     full assessment must not leak to suppliers).
 *
 * Pure module with ZERO imports beyond the sibling taxonomy constants: the
 * jsPDF constructor is dependency-injected (each consumer resolves jspdf from
 * its own node_modules) and the document surface is typed structurally below.
 */
import { CAT_LABEL, CAT_DESC } from './fix-guide-taxonomy';
import {
  SUPPLIER_FRAMING_HEADING,
  SUPPLIER_FRAMING_BODY,
  SUPPLIER_FIELD_LABELS,
} from './fix-guide-supplier-copy';
import { NEXA_LOGO_PNG_BASE64, NEXA_LOGO_DIMENSIONS } from './nexa-logo-png';

/** The slice of the jsPDF API these builders use (structural — no jspdf import). */
export interface PdfDocLike {
  /** Optional — jsPDF's image embed. Absent on minimal fakes; the header
   *  falls back to the text wordmark WITH a loud stderr warning (failure
   *  mode #4: silent asset fallbacks are banned). */
  addImage?(data: string, format: string, x: number, y: number, w: number, h: number, alias?: string, compression?: string): unknown;
  setFontSize(size: number): unknown;
  setFont(family: string, style: string): unknown;
  setTextColor(r: number, g: number, b: number): unknown;
  setFillColor(r: number, g: number, b: number): unknown;
  setDrawColor(r: number, g: number, b: number): unknown;
  setLineWidth(w: number): unknown;
  splitTextToSize(text: string, maxWidth: number): string[];
  text(text: string | string[], x: number, y: number): unknown;
  addPage(): unknown;
  rect(x: number, y: number, w: number, h: number, style?: string): unknown;
  roundedRect(x: number, y: number, w: number, h: number, rx: number, ry: number, style?: string): unknown;
  line(x1: number, y1: number, x2: number, y2: number): unknown;
  getTextWidth(text: string): number;
  /** Section-ring primitives (monthly summary at-a-glance). Optional so minimal
   *  fakes need not implement them; the builder guards each call and the ring
   *  arc degrades to nothing on a fake that omits them (still emits the text). */
  circle?(x: number, y: number, r: number, style?: string): unknown;
  triangle?(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, style?: string): unknown;
  setLineCap?(style: string | number): unknown;
  /** Page iteration for the per-page brand strip (UAT 13 October 2026, finding 6). Optional so
   *  minimal fakes need not implement them; without them only page 1 carries the brand. */
  getNumberOfPages?(): number;
  setPage?(n: number): unknown;
}

/**
 * The SUBSCRIBER's brand for a report header (UAT 13 October 2026, finding 6 — "the report is
 * showing Nexanomics branding, but I am clearly logged in as Cybercraft"). The report is the
 * subscriber's document: their Brand Presence logo leads; Nexanomics is a small "Powered by".
 * `logo` is a PNG/JPEG the caller has already read (jsPDF embeds neither SVG nor WEBP), with its
 * pixel size for the aspect ratio; null → the business name in text.
 */
export interface PdfBrand {
  name: string;
  logo?: { base64: string; format: 'PNG' | 'JPEG'; width: number; height: number } | null;
}

export type PdfDocFactory = () => PdfDocLike;

export interface PdfGradeTile {
  label: string;
  score: number;
  grade: string;
  descriptor: string;
  colour: [number, number, number];
}

export interface PdfCard {
  refLabel: string;
  title: string;
  widget: string;
  detection: string;
  diagnosis: string;
  direction: string;
  priority: string; // high | medium | low
  estimatedPoints: number;
  assignee: string;
  assignedOn: string | null;
  status: string;
  category: string;
}

export function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace('#', '');
  return [parseInt(m.slice(0, 2), 16), parseInt(m.slice(2, 4), 16), parseInt(m.slice(4, 6), 16)];
}

const PRI_COL: Record<string, [number, number, number]> = {
  high: [220, 38, 38], medium: [180, 83, 9], low: [21, 128, 61],
};

const UNASSIGNED = 'Unassigned';

// ── Shared helpers ───────────────────────────────────────────────────────────

export interface Cursor { y: number }

export function makeWrap(doc: PdfDocLike, cur: Cursor, W: number, M: number) {
  return (t: string, size: number, style: string, colour: [number, number, number], x: number, maxW?: number) => {
    doc.setFontSize(size);
    doc.setFont('helvetica', style);
    doc.setTextColor(...colour);
    const lines = doc.splitTextToSize(t, maxW ?? W - 2 * M - (x - M));
    lines.forEach((l) => {
      if (cur.y > 278) { doc.addPage(); cur.y = 20; }
      doc.text(l, x, cur.y);
      cur.y += size * 0.46;
    });
  };
}

/**
 * The branded header band (P14, 12 June 2026): dark band, Nexanomics logo
 * left-aligned, "CUSTOMER JOURNEY INTELLIGENCE" tagline right-aligned, then
 * the document title + subtitle. Shared by BOTH the owner report and the
 * supplier attachment so the brand treatment can never diverge.
 */
export function headerBand(doc: PdfDocLike, W: number, M: number, title: string, subtitle: string, brand?: PdfBrand | null) {
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, W, 32, 'F');
  if (brand) {
    subscriberBrandRow(doc, W, M, brand);
    titleRows(doc, M, title, subtitle);
    return;
  }

  // Brand row — real logo asset; loud warning + text wordmark on any failure
  // (failure mode #4: an asset loader must never fall back silently).
  const logoH = 5.5;
  const logoW = logoH * (NEXA_LOGO_DIMENSIONS.width / NEXA_LOGO_DIMENSIONS.height);
  let logoDrawn = false;
  if (doc.addImage && NEXA_LOGO_PNG_BASE64.length > 100) {
    try {
      // FAST compression: jsPDF stores RGBA PNGs as raw pixel data (~283KB for
      // this asset) unless told to Flate the stream — 287KB → ~30KB attachment.
      doc.addImage(NEXA_LOGO_PNG_BASE64, 'PNG', M, 5, logoW, logoH, undefined, 'FAST');
      logoDrawn = true;
    } catch (err) {
      console.error('fix-guide-pdf: LOGO EMBED FAILED — falling back to the text wordmark. Treat as a delivery blocker.', err);
    }
  } else {
    console.error('fix-guide-pdf: LOGO ASSET UNAVAILABLE (addImage or base64 missing) — text wordmark fallback. Treat as a delivery blocker.');
  }
  if (!logoDrawn) {
    doc.setTextColor(241, 245, 249);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('nexanomics', M, 9.5);
  }
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(148, 163, 184);
  const tagline = 'CUSTOMER JOURNEY INTELLIGENCE';
  doc.text(tagline, W - M - doc.getTextWidth(tagline), 9.5);

  titleRows(doc, M, title, subtitle);
}

function titleRows(doc: PdfDocLike, M: number, title: string, subtitle: string) {
  doc.setTextColor(241, 245, 249);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text(title, M, 20);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(148, 163, 184);
  doc.text(subtitle, M, 26.5);
}

/** The subscriber's logo (or name) left; "Powered by Nexanomics" small, right. */
function subscriberBrandRow(doc: PdfDocLike, W: number, M: number, brand: PdfBrand) {
  let drawn = false;
  const logo = brand.logo;
  if (logo && doc.addImage && logo.width > 0 && logo.height > 0) {
    // Fit inside 7 mm tall × 60 mm wide, keeping the aspect ratio.
    const h = Math.min(7, 60 * (logo.height / logo.width));
    const w = h * (logo.width / logo.height);
    try {
      doc.addImage(`data:image/${logo.format.toLowerCase()};base64,${logo.base64}`, logo.format, M, 3.5, w, h, undefined, 'FAST');
      drawn = true;
    } catch (err) {
      // Loud, never silent (failure mode #4) — the name in text is the honest fallback.
      console.error('report-pdf: SUBSCRIBER LOGO EMBED FAILED — business name in text instead.', err);
    }
  }
  if (!drawn) {
    doc.setTextColor(241, 245, 249);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text(brand.name || 'Your business', M, 9.5);
  }
  poweredBy(doc, W, M, 9.5, [148, 163, 184]);
}

/** "Powered by Nexanomics" — small, right-aligned at `y`. */
function poweredBy(doc: PdfDocLike, W: number, M: number, y: number, colour: [number, number, number]) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...colour);
  const t = 'Powered by Nexanomics';
  doc.text(t, W - M - doc.getTextWidth(t), y);
}

/**
 * Every page after the first carries the subscriber's name top-left and "Powered by Nexanomics"
 * top-right (page 1 has the full header band). Needs page iteration; a fake without it keeps the
 * page-1 band only.
 */
export function stampBrandOnPages(doc: PdfDocLike, W: number, M: number, brand: PdfBrand) {
  if (!doc.getNumberOfPages || !doc.setPage) return;
  const n = doc.getNumberOfPages();
  for (let p = 2; p <= n; p += 1) {
    doc.setPage(p);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    doc.text(brand.name || 'Your business', M, 10);
    poweredBy(doc, W, M, 10, [100, 116, 139]);
  }
  doc.setPage(n);
}

/** Owner-report card pages — full anatomy incl. diagnosis + workflow line. */
function cardDetailPages(
  doc: PdfDocLike,
  cur: Cursor,
  M: number,
  cards: PdfCard[],
  includeHow: boolean,
  wrap: ReturnType<typeof makeWrap>,
) {
  cards.forEach((c) => {
    if (cur.y > 262) { doc.addPage(); cur.y = 20; }
    doc.setDrawColor(99, 102, 241);
    doc.setLineWidth(0.8);
    const yStart = cur.y - 4;
    wrap(`${c.refLabel}   ${c.title}`, 12, 'bold', [15, 23, 42], M + 4);
    wrap(c.widget, 9, 'normal', [100, 116, 139], M + 4);
    cur.y += 1;
    wrap(`Detected:  ${c.detection}`, 10, 'normal', [51, 65, 85], M + 4);
    wrap(`Why it matters:  ${c.diagnosis}`, 10, 'italic', [51, 65, 85], M + 4);
    if (includeHow) wrap(`How to action:  ${c.direction}`, 10, 'normal', [15, 83, 50], M + 4);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const pc = PRI_COL[c.priority] ?? PRI_COL.medium;
    doc.setTextColor(...pc);
    if (cur.y > 278) { doc.addPage(); cur.y = 20; }
    doc.text(c.priority.toUpperCase(), M + 4, cur.y);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(
      `· est. +${c.estimatedPoints} pts · assignee: ${c.assignee}${c.assignedOn ? ` (assigned ${c.assignedOn})` : ''} · status: ${c.status}`,
      M + 4 + doc.getTextWidth(c.priority.toUpperCase()) + 2,
      cur.y,
    );
    cur.y += 3;
    doc.line(M, yStart, M, cur.y - 2);
    cur.y += 6;
  });
}

// ── OWNER report (D9 as amended 12 June 2026) ───────────────────────────────

export interface OwnerPdfData {
  /** e.g. "Fix Cards" / "Mobile Health: Fix Cards" (TAB_LABEL). */
  scopeLabel: string;
  hostname: string;
  reportMonthLabel: string; // "May 2026"
  generatedDate: string; // "12 June 2026" — full day date, never month-only
  tiles: PdfGradeTile[];
  /**
   * The FULL card set for the current section — page 1 summarises its open
   * cards regardless of any filter/selection (filter-independence rule).
   */
  summaryCards: PdfCard[];
  /** The card pages reflect the active filter/selection. */
  detailCards: PdfCard[];
  /** Supplier registry short names — classifies assignees supplier vs internal. */
  supplierNames: string[];
  /** Non-null when detailCards is a filtered/selected subset — the page-1 scope note. */
  scopedCount: number | null;
  includeHow: boolean;
}

/** Kept name — the portal's Report PDF modal builder. */
export function buildFixGuidePdfCore(createDoc: PdfDocFactory, data: OwnerPdfData): PdfDocLike {
  const open = data.summaryCards.filter((c) => c.status !== 'done');
  const n: Record<string, number> = { high: 0, medium: 0, low: 0 };
  open.forEach((c) => { n[c.priority] = (n[c.priority] ?? 0) + 1; });
  const gain = open.reduce((s, c) => s + c.estimatedPoints, 0);

  const doc = createDoc();
  const W = 210;
  const M = 16;
  const cur: Cursor = { y: 20 };
  const wrap = makeWrap(doc, cur, W, M);

  headerBand(
    doc, W, M,
    `Fix Guide — ${data.scopeLabel.replace(': Fix Cards', '')}`,
    `${data.hostname}  ·  ${data.reportMonthLabel} report  ·  Generated ${data.generatedDate}  ·  Nexanomics Customer Journey Intelligence`,
  );

  // ── PAGE 1: the Owner's one-pager — ALWAYS the full open set ──
  cur.y = 42;
  wrap('Your website at a glance', 16, 'bold', [15, 23, 42], M);
  cur.y += 3;

  const tY = cur.y;
  data.tiles.forEach((t, i) => {
    const x = M + i * 52;
    doc.setDrawColor(...t.colour);
    doc.setLineWidth(1);
    doc.roundedRect(x, tY, 46, 30, 2.5, 2.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(...t.colour);
    doc.text(t.grade, x + 9, tY + 19);
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text(t.label, x + 20, tY + 12);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text(`${t.score} / 100`, x + 20, tY + 18);
    doc.text(t.descriptor, x + 20, tY + 24);
  });

  // Potential gain box (full open set)
  const byCat: Record<string, number> = {};
  open.forEach((c) => { byCat[c.category] = (byCat[c.category] ?? 0) + 1; });
  const top = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0];
  doc.setDrawColor(22, 163, 74);
  doc.setLineWidth(0.8);
  doc.roundedRect(M + 108, tY, 86, 30, 2.5, 2.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(17);
  doc.setTextColor(22, 163, 74);
  doc.text(`+${gain} pts`, M + 114, tY + 12);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(
    doc.splitTextToSize(
      `available if all ${open.length} open fixes are actioned (${n.high} high · ${n.medium} medium · ${n.low} low priority)`,
      76,
    ),
    M + 114, tY + 18,
  );
  cur.y = tY + 38;

  if (top) {
    doc.setDrawColor(99, 102, 241);
    doc.setLineWidth(0.8);
    doc.roundedRect(M, cur.y, 178, 22, 2.5, 2.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(67, 56, 202);
    doc.text(`Biggest opportunity: ${CAT_LABEL[top[0]] ?? top[0]}  (${top[1]} open fixes)`, M + 5, cur.y + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text(doc.splitTextToSize(CAT_DESC[top[0]] ?? '', 168), M + 5, cur.y + 14);
    cur.y += 30;
  }

  // Priority bar — High → Medium → Low, ALL THREE labelled, zeros shown.
  wrap('Open fixes by priority', 12, 'bold', [15, 23, 42], M);
  cur.y += 1;
  const barY = cur.y;
  const barW = 178;
  let bx = M;
  (['high', 'medium', 'low'] as const).forEach((k) => {
    const v = n[k];
    if (!v || open.length === 0) return;
    const w = (barW * v) / open.length;
    doc.setFillColor(...PRI_COL[k]);
    doc.rect(bx, barY, w, 8, 'F');
    bx += w;
  });
  cur.y = barY + 13;
  // The counts line is the authoritative labelling — independent of segment
  // widths, so a zero never silently vanishes (UAT: a lone "LOW 2" bar).
  doc.setFontSize(10);
  let lx = M;
  (['high', 'medium', 'low'] as const).forEach((k) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRI_COL[k]);
    const label = `${k[0].toUpperCase()}${k.slice(1)} ${n[k]}`;
    doc.text(label, lx, cur.y);
    lx += doc.getTextWidth(label) + 10;
  });
  cur.y += 9;

  // Fix Cards by Assignee (replaces the category-score spread — D9 amendment).
  wrap('Fix cards by assignee', 12, 'bold', [15, 23, 42], M);
  cur.y += 2;
  const byAssignee = new Map<string, { open: number; high: number }>();
  open.forEach((c) => {
    const row = byAssignee.get(c.assignee) ?? { open: 0, high: 0 };
    row.open += 1;
    if (c.priority === 'high') row.high += 1;
    byAssignee.set(c.assignee, row);
  });
  const supplierSet = new Set(data.supplierNames);
  const rows = [...byAssignee.entries()].sort((a, b) => {
    // Unassigned first (the call-out), then by open count descending.
    if (a[0] === UNASSIGNED) return -1;
    if (b[0] === UNASSIGNED) return 1;
    return b[1].open - a[1].open;
  });
  rows.forEach(([name, row]) => {
    if (cur.y > 270) { doc.addPage(); cur.y = 20; }
    const isUn = name === UNASSIGNED;
    const tag = isUn ? 'needs an owner' : supplierSet.has(name) ? 'supplier' : 'internal';
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...(isUn ? [217, 119, 6] as [number, number, number] : [15, 23, 42] as [number, number, number]));
    doc.text(name, M, cur.y);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(`(${tag})`, M + doc.getTextWidth(name) + 3, cur.y);
    doc.setTextColor(71, 85, 105);
    doc.text(`${row.open} open`, M + 120, cur.y);
    doc.setTextColor(...PRI_COL.high);
    doc.text(`${row.high} high`, M + 150, cur.y);
    cur.y += 6.5;
  });

  // Scope note — card detail may be filtered; the summary above never is.
  if (data.scopedCount !== null) {
    cur.y += 4;
    wrap(
      `Card detail in this document is scoped to ${data.scopedCount} selected/filtered card${data.scopedCount === 1 ? '' : 's'}.`,
      9, 'italic', [148, 163, 184], M,
    );
  }

  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(148, 163, 184);
  doc.text(
    `From your ${data.reportMonthLabel} Nexanomics assessment of ${data.hostname}. Fix details follow on the next pages.`,
    M, 290,
  );

  // ── PAGE 2+: fix cards (the active filter/selection) ──
  doc.addPage();
  cur.y = 20;
  wrap('Fix cards', 14, 'bold', [15, 23, 42], M);
  cur.y += 2;
  cardDetailPages(doc, cur, M, data.detailCards, data.includeHow, wrap);

  return doc;
}

// ── SUPPLIER attachment (D7 as amended 12 June 2026 — work-only) ────────────

export interface SupplierPdfData {
  hostname: string;
  /** Owner identity for the "on behalf of" line — businessName, else hostname. */
  businessName: string;
  reportMonthLabel: string;
  generatedDate: string;
  expiresOn: string; // "15 June 2026"
  cards: PdfCard[];
  /** ADR-059 Amendment 3 (A3.5) — a work order, not an invitation to quote: the heading, the reply line
   *  and the date line say so (the authority line, verbatim from the email). The work detail is unchanged. */
  workOrder?: { authority: string };
}

/**
 * The invitation-email attachment. Quoting-grade work detail with neutral
 * framing (UAT punchlist 2 — P4/P5): per card Ref + Title, Finding (the full
 * detection evidence), Why it matters (the diagnosis — context for the
 * professional), Recommended fix (the direction, as the engine emits it —
 * never padded), Priority. STILL EXCLUDED (the judging surface): grades,
 * scores, category bars, estimatedPoints, assignee data, owner-side
 * intelligence.
 */
export function buildSupplierWorkPdfCore(createDoc: PdfDocFactory, data: SupplierPdfData): PdfDocLike {
  const doc = createDoc();
  const W = 210;
  const M = 16;
  const cur: Cursor = { y: 20 };
  const wrap = makeWrap(doc, cur, W, M);

  headerBand(
    doc, W, M,
    data.workOrder ? 'Work order — fix detail' : 'Invitation to Quote — work detail',
    `On behalf of ${data.businessName || data.hostname}  ·  ${data.hostname}  ·  Prepared ${data.generatedDate}  ·  Nexanomics`,
  );

  cur.y = 42;
  wrap(
    data.workOrder
      ? `${data.businessName || data.hostname} would like you to fix the ${data.cards.length} website item${data.cards.length === 1 ? '' : 's'} below.`
      : `${data.businessName || data.hostname} is inviting you to quote on the ${data.cards.length} website fix${data.cards.length === 1 ? '' : 'es'} below.`,
    11, 'normal', [15, 23, 42], M,
  );
  cur.y += 2;

  // Neutral provenance framing (P5) — single-sourced with the email body.
  doc.setDrawColor(99, 102, 241);
  doc.setLineWidth(0.5);
  const frameTop = cur.y - 3;
  wrap(`${SUPPLIER_FRAMING_HEADING}: ${SUPPLIER_FRAMING_BODY}`, 9, 'italic', [71, 85, 105], M + 3, W - 2 * M - 6);
  doc.roundedRect(M, frameTop, W - 2 * M, cur.y - frameTop + 2, 2, 2);
  cur.y += 6;

  if (data.workOrder) {
    wrap(data.workOrder.authority, 10, 'normal', [67, 56, 202], M);
    wrap('Just reply to the work order email — plain language is fine.', 9, 'italic', [100, 116, 139], M);
  } else {
    wrap(
      'To quote: just reply to the invitation email with your price for the bundle and which items you can do — plain language is fine.',
      10, 'normal', [67, 56, 202], M,
    );
    wrap(`This invitation expires on ${data.expiresOn} (3 days).`, 9, 'italic', [100, 116, 139], M);
  }
  cur.y += 4;

  data.cards.forEach((c) => {
    if (cur.y > 255) { doc.addPage(); cur.y = 20; }
    doc.setDrawColor(99, 102, 241);
    doc.setLineWidth(0.8);
    const yStart = cur.y - 4;
    wrap(`${c.refLabel}   ${c.title}`, 12, 'bold', [15, 23, 42], M + 4);
    cur.y += 1;
    wrap(`${SUPPLIER_FIELD_LABELS.finding}:  ${c.detection}`, 10, 'normal', [51, 65, 85], M + 4);
    wrap(`${SUPPLIER_FIELD_LABELS.whyItMatters}:  ${c.diagnosis}`, 10, 'italic', [51, 65, 85], M + 4);
    wrap(`${SUPPLIER_FIELD_LABELS.recommendedFix}:  ${c.direction}`, 10, 'normal', [15, 83, 50], M + 4);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    const pc = PRI_COL[c.priority] ?? PRI_COL.medium;
    doc.setTextColor(...pc);
    if (cur.y > 278) { doc.addPage(); cur.y = 20; }
    doc.text(`${SUPPLIER_FIELD_LABELS.priority}: ${c.priority.toUpperCase()}`, M + 4, cur.y);
    cur.y += 3;
    doc.line(M, yStart, M, cur.y - 2);
    cur.y += 6;
  });

  return doc;
}

// ── MONTHLY SUMMARY report (ADR-068) ─────────────────────────────────────────
// The subscriber-facing exec-summary report UAT expected. ONE engine with the
// Fix Guide owner report (D1.1 — shares this module's headerBand / makeWrap /
// cardDetailPages / priority + assignee logic so the two cannot drift). The
// at-a-glance page is a RINGS variant of "Your website at a glance": section
// rings + grades + a banded trend arrow + the honest building-states note,
// then biggest opportunity → open-fixes-by-priority → optional fix-card
// appendix (the toggle, D4).

/** The verbatim building-states note + tooltip "why" (ADR-068 D2.3 / D2.4).
 *  "profile", not "data" — a profile builds. Exported so the live scorecard's
 *  copy-forked tooltip (Structure Guide §9) renders the SAME words. */
export const BUILDING_NOTE =
  'Customer Journey Intelligence builds over time — scored sections are shown; the rest are building your journey profile.';
export const BUILDING_TOOLTIP =
  'Customer Journey Intelligence requires data over time; sections populate as intelligence accrues.';

const RING_TRACK_RGB: [number, number, number] = [226, 232, 240]; // --track (#e2e8f0)
const TREND_UP_RGB: [number, number, number] = [22, 163, 74];     // green ▲
const TREND_DOWN_RGB: [number, number, number] = [220, 38, 38];   // red ▼
const TREND_STEADY_RGB: [number, number, number] = [100, 116, 139]; // slate — steady

/** Polar → cartesian, 0° at 12 o'clock — ported from ring-geometry.ts toXY so
 *  the PDF rings match the dashboard's geometry without coupling portal-shared
 *  to portal-subscriber (Structure Guide §9). */
function ringXY(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

/** A single scored section ring (jsPDF can't stroke an SVG path, so the scored
 *  arc is drawn as a thick round-capped polyline sampled along the sweep —
 *  sweep = score/100 × 360°, the same proportion as ScorecardRing). */
function drawRing(
  doc: PdfDocLike, cx: number, cy: number, r: number, sw: number,
  score: number, colour: [number, number, number],
) {
  // Track — full grey circle at the stroke width.
  if (doc.circle) {
    doc.setDrawColor(...RING_TRACK_RGB);
    doc.setLineWidth(sw);
    doc.circle(cx, cy, r, 'S');
  }
  // Scored arc — round caps + fine sampling so segment joints don't gap.
  const sweep = Math.max(0, Math.min(100, score)) / 100 * 360;
  if (sweep <= 0) return;
  doc.setDrawColor(...colour);
  doc.setLineWidth(sw);
  doc.setLineCap?.('round');
  const step = 5;
  let prev = ringXY(cx, cy, r, 0);
  for (let a = step; a <= sweep + 0.001; a += step) {
    const next = ringXY(cx, cy, r, Math.min(a, sweep));
    doc.line(prev.x, prev.y, next.x, next.y);
    prev = next;
  }
  doc.setLineCap?.('butt');
}

/** A small trend glyph (▲/▼/steady dash) beside a ring, with the raw delta
 *  caption ("+6 from start of month") — the static-PDF form of the on-hover
 *  number (ADR-068 D2.5). */
function drawTrend(
  doc: PdfDocLike, x: number, y: number,
  trend: { direction: 'up' | 'down' | 'steady'; delta: number },
) {
  const g = 1.8; // glyph half-size (mm)
  if (trend.direction === 'up' && doc.triangle) {
    doc.setFillColor(...TREND_UP_RGB);
    doc.triangle(x, y - g, x - g, y + g, x + g, y + g, 'F');
  } else if (trend.direction === 'down' && doc.triangle) {
    doc.setFillColor(...TREND_DOWN_RGB);
    doc.triangle(x, y + g, x - g, y - g, x + g, y - g, 'F');
  } else {
    // steady — a short dash
    doc.setDrawColor(...TREND_STEADY_RGB);
    doc.setLineWidth(0.8);
    doc.line(x - g, y, x + g, y);
  }
  const caption = trend.direction === 'steady'
    ? 'steady this month'
    : `${trend.delta > 0 ? '+' : ''}${trend.delta} from start of month`;
  const rgb = trend.direction === 'up' ? TREND_UP_RGB : trend.direction === 'down' ? TREND_DOWN_RGB : TREND_STEADY_RGB;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...rgb);
  doc.text(caption, x + g + 2, y + 1);
}

export interface PdfRing {
  label: string;              // "Mobile" / "Desktop"
  score: number;              // 0–100
  grade: string;              // letter grade
  descriptor: string;         // "Failing" / "Satisfactory" …
  colour: [number, number, number]; // grade colour (rgb)
  trend: { direction: 'up' | 'down' | 'steady'; delta: number; steady: boolean };
}

/**
 * ADR-096 Amendment 4 (journeys punchlist 2, PL-5) — the Journeys / Abandonment
 * material for the TRUST section report. This is the report's journeys content:
 * the classified-journey total + movement, where journeys stop (the six terminal
 * states), how each real source converts, and the referring sites. Present only
 * when the domain has classified journeys; absent → the section is omitted (the
 * report degrades honestly, never an empty heading). Reputation folds into this
 * same section later with no rework.
 */
export interface PdfJourneys {
  totalCurrent: number;
  /** Ring-centre movement caption (e.g. "Improving · fewer stops"). */
  movementCaption: string;
  /** True once a prior period exists (first month → no comparison). */
  hasPrior: boolean;
  /** The six terminal states in canonical order, with this month's counts. */
  stopPoints: Array<{ label: string; count: number; completion?: boolean }>;
  /** Real (at/above-floor) sources, most visitors first: how each converts. */
  sources: Array<{ name: string; channelLabel: string; visitors: number; reachedOutPct: number }>;
  /** Sub-floor sources acknowledged as Inactive (no rate — PL-1). */
  inactiveSources: Array<{ name: string; channelLabel: string; visitors: number }>;
  /** Top referring sites (host-level; sessions + contacts). */
  referrers: Array<{ host: string; sessions: number; contacts: number }>;
}

export interface MonthlySummaryData {
  /** ADR-096 Amendment 5 (PL3-5) — the report SECTION scope. Each section's
   *  Download emits ONLY its own content: `website` → the website spine + fix
   *  appendix (no journeys); `trust` → the journeys/abandonment material only (no
   *  website spine, no fix cards); `discovery` → discovery only (placeholder
   *  until it gets a row). Absent → `website` (back-compat). Mirrors the
   *  `ReportSection` type in lambdas/shared/src/report-storage (kept as an inline
   *  literal so portal-shared stays free of a cross-package import). */
  section?: 'website' | 'trust' | 'discovery';
  hostname: string;
  businessName: string;
  reportMonthLabel: string;   // "June 2026"
  generatedDate: string;      // "22 June 2026" — full day date, never month-only
  /** Scored sections only (D2.1) — no empty rings for unscored sections. */
  rings: PdfRing[];
  /** The full open card set — drives biggest-opportunity, gain, and priority
   *  counts on the summary spine (filter-independent, like the owner report). */
  summaryCards: PdfCard[];
  supplierNames: string[];
  /** Fix-card appendix (D1.3) — rendered only when `includeFixes` (D4 toggle). */
  includeFixes: boolean;
  appendixCards: PdfCard[];
  includeHow: boolean;
  /** PL-5 — the TRUST section's journeys/abandonment material. Omitted when the
   *  domain has no classified journeys yet. */
  journeys?: PdfJourneys | null;
  /** The subscriber's brand for the header and every page (UAT 13 October 2026, finding 6).
   *  Absent → the legacy Nexanomics header (old callers, tests). */
  brand?: PdfBrand | null;
  /** ADR-128 §3.5 — which scoring profile was used and why, one line. Absent → omitted. */
  profileLine?: string | null;
  /** ADR-128 Amendment 2 — "Do these N fixes to reach A", per viewport. Absent → omitted. */
  pathToA?: PdfPathToA[] | null;
}

/** ADR-128 Amendment 2 — one viewport's fixes to reach A (≥ 90), in order. */
export interface PdfPathToA {
  label: string;      // "Mobile" | "Desktop"
  current: number;
  projected: number;
  reached: boolean;
  reachable: boolean;
  steps: Array<{ title: string; recovers: number }>;
}

/**
 * ADR-096 Amendment 4 (PL-5) — the Journeys section. A dedicated page: the
 * classified-journey total + movement, the six stop points (where journeys end),
 * how each real source converts (reached-out %), Inactive sources acknowledged
 * without a rate (PL-1), and the referring sites. Text + simple bars only (jsPDF
 * can't stroke the portal's SVG ring), so it reads as a faithful summary of the
 * on-screen drilldown.
 */
function journeysBody(
  doc: PdfDocLike, W: number, M: number, j: PdfJourneys,
  wrap: (t: string, size: number, style: string, colour: [number, number, number], x: number, maxW?: number) => void,
  cur: Cursor,
) {
  // Renders at the current cursor — the caller decides the page break (the
  // Website report has none today; the Trust report renders this as its first
  // page, ADR-096 Amendment 5 PL3-5).
  wrap('Your journeys this month', 14, 'bold', [15, 23, 42], M);
  cur.y += 1;
  const total = j.totalCurrent;
  const sub = `${total} classified journey${total === 1 ? '' : 's'}`
    + (j.hasPrior ? ` · ${j.movementCaption}` : ' · first month — building a baseline to compare against');
  wrap(sub, 10, 'normal', [71, 85, 105], M);
  cur.y += 4;

  // ── Where journeys stop (the six terminal states) ──
  wrap('Where journeys stop', 11, 'bold', [15, 23, 42], M);
  cur.y += 2;
  const barX = M + 74;
  const barMaxW = W - M - barX - 14;
  const maxCount = Math.max(1, ...j.stopPoints.map((s) => s.count));
  j.stopPoints.forEach((s) => {
    if (cur.y > 268) { doc.addPage(); cur.y = 20; }
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(51, 65, 85);
    doc.text(doc.splitTextToSize(s.label, 66)[0], M, cur.y + 3);
    const w = Math.max(0.6, (barMaxW * s.count) / maxCount);
    // Completion (reached out) is green; the abandonment stops are amber.
    if (s.completion) doc.setFillColor(22, 163, 74); else doc.setFillColor(180, 83, 9);
    doc.rect(barX, cur.y, w, 4, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(71, 85, 105);
    doc.text(String(s.count), barX + w + 2, cur.y + 3.6);
    cur.y += 8;
  });
  cur.y += 4;

  // ── How each source converts ──
  wrap('How each source converts', 11, 'bold', [15, 23, 42], M);
  cur.y += 2;
  if (j.sources.length === 0) {
    wrap('No source has enough traffic yet to chart a journey.', 9, 'italic', [100, 116, 139], M);
  } else {
    j.sources.forEach((s) => {
      if (cur.y > 272) { doc.addPage(); cur.y = 20; }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9.5);
      doc.setTextColor(15, 23, 42);
      doc.text(s.name, M, cur.y);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(100, 116, 139);
      doc.text(`${s.channelLabel} · ${s.visitors} visitor${s.visitors === 1 ? '' : 's'}`, M + doc.getTextWidth(s.name) + 4, cur.y);
      const rate = `${s.reachedOutPct.toFixed(1)}% reached out`;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(22, 163, 74);
      doc.text(rate, W - M - doc.getTextWidth(rate), cur.y);
      cur.y += 6;
    });
  }
  if (j.inactiveSources.length > 0) {
    cur.y += 1;
    const names = j.inactiveSources.map((s) => `${s.name} (${s.visitors})`).join(', ');
    wrap(`Inactive — too few visitors to chart a journey: ${names}.`, 8.5, 'italic', [100, 116, 139], M);
  }
  cur.y += 4;

  // ── Referring sites ──
  if (j.referrers.length > 0) {
    wrap('Referring sites', 11, 'bold', [15, 23, 42], M);
    cur.y += 2;
    j.referrers.forEach((rf) => {
      if (cur.y > 276) { doc.addPage(); cur.y = 20; }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      doc.text(rf.host, M, cur.y);
      const detail = `${rf.sessions} visitor${rf.sessions === 1 ? '' : 's'} · ${rf.contacts} reached out`;
      doc.setTextColor(100, 116, 139);
      doc.text(detail, W - M - doc.getTextWidth(detail), cur.y);
      cur.y += 6;
    });
  }
}

/**
 * Build the subscriber monthly summary report. Pure — the jsPDF constructor is
 * injected. Page 1 is the rings at-a-glance; the Journeys page (PL-5) follows
 * when the domain has classified journeys; the fix-card appendix follows only
 * when the toggle is on (D4). Returns the document for the caller to output.
 */
/** "June 2026" stays as is; "First assessment · 20 October 2026" reads "first" in a sentence. */
function periodPhrase(label: string): string {
  return /^First assessment/.test(label) ? 'first' : label;
}

const SECTION_TITLE: Record<'website' | 'trust' | 'discovery', string> = {
  website: 'Website Report',
  trust: 'Trust Report',
  discovery: 'Discovery Report',
};

export function buildMonthlySummaryPdfCore(createDoc: PdfDocFactory, data: MonthlySummaryData): PdfDocLike {
  const doc = monthlySummaryBody(createDoc, data);
  if (data.brand) stampBrandOnPages(doc, 210, 16, data.brand);
  return doc;
}

function monthlySummaryBody(createDoc: PdfDocFactory, data: MonthlySummaryData): PdfDocLike {
  // ADR-096 Amendment 5 (PL3-5) — section-scoped: each Download emits ONLY its
  // own section. `website` is the default (back-compat with existing callers).
  const section = data.section ?? 'website';

  const open = data.summaryCards.filter((c) => c.status !== 'done');
  const n: Record<string, number> = { high: 0, medium: 0, low: 0 };
  open.forEach((c) => { n[c.priority] = (n[c.priority] ?? 0) + 1; });
  const gain = open.reduce((s, c) => s + c.estimatedPoints, 0);

  const doc = createDoc();
  const W = 210;
  const M = 16;
  const cur: Cursor = { y: 20 };
  const wrap = makeWrap(doc, cur, W, M);

  headerBand(
    doc, W, M,
    `${SECTION_TITLE[section]} — ${data.reportMonthLabel}`,
    data.brand
      ? `${data.hostname}  ·  Generated ${data.generatedDate}`
      : `${data.hostname}  ·  Generated ${data.generatedDate}  ·  Nexanomics Customer Journey Intelligence`,
    data.brand,
  );

  // ── TRUST report — the journeys/abandonment material ONLY (no website spine,
  //    no fix cards). Rendered on the first page. ──
  if (section === 'trust') {
    cur.y = 44;
    if (data.journeys && data.journeys.totalCurrent > 0) {
      journeysBody(doc, W, M, data.journeys, wrap, cur);
    } else {
      wrap('Your journeys this month', 16, 'bold', [15, 23, 42], M);
      cur.y += 2;
      wrap(
        'No classified journeys yet this month. As visits arrive and complete their '
        + 'paths, this report fills in where each source’s journeys stop and who reaches out.',
        10, 'normal', [71, 85, 105], M, W - 2 * M,
      );
    }
    return doc;
  }

  // ── DISCOVERY report — placeholder until the section gets a scoring engine. ──
  if (section === 'discovery') {
    cur.y = 44;
    wrap('Discovery', 16, 'bold', [15, 23, 42], M);
    cur.y += 2;
    wrap('Discovery reporting is coming soon.', 10, 'normal', [71, 85, 105], M, W - 2 * M);
    return doc;
  }

  // ── WEBSITE report — the website spine + fix-card appendix. NO journeys. ──
  // ── PAGE 1: rings at-a-glance ──
  cur.y = 44;
  wrap('Your website at a glance', 16, 'bold', [15, 23, 42], M);
  cur.y += 4;

  // Section rings, laid out left→right. Scored sections only (D2.1).
  const ringR = 15;
  const ringSw = 5;
  const ringCy = cur.y + ringR + 4;
  const slotW = 89;
  data.rings.forEach((ring, i) => {
    const cx = M + 26 + i * slotW;
    drawRing(doc, cx, ringCy, ringR, ringSw, ring.score, ring.colour);
    // Grade letter centred in the ring.
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...ring.colour);
    doc.text(ring.grade, cx - doc.getTextWidth(ring.grade) / 2, ringCy + 2);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(71, 85, 105);
    const sc = `${ring.score}/100`;
    doc.text(sc, cx - doc.getTextWidth(sc) / 2, ringCy + 8);
    // Label + descriptor to the right of the ring.
    const tx = cx + ringR + 6;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(ring.label, tx, ringCy - 5);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    doc.text(ring.descriptor, tx, ringCy + 1);
    // Trend glyph + caption below the label.
    drawTrend(doc, tx + 2, ringCy + 7, ring.trend);
  });
  cur.y = ringCy + ringR + 8;

  // ADR-128 — the profile line, then the fixes that reach A for each viewport.
  if (data.profileLine) {
    wrap(data.profileLine, 9.5, 'italic', [51, 65, 85], M, W - 2 * M);
    cur.y += 3;
  }
  for (const p of data.pathToA ?? []) {
    if (p.reached) {
      wrap(`${p.label}: ${p.current}/100 — already an A.`, 11, 'bold', [22, 101, 52], M);
      cur.y += 2;
      continue;
    }
    if (!p.reachable || p.steps.length === 0) continue;
    const n = p.steps.length;
    wrap(`${p.label}: do ${n === 1 ? 'this fix' : `these ${n} fixes`} to reach A (${p.current} → ${p.projected})`, 11, 'bold', [15, 23, 42], M);
    p.steps.forEach((st, i) => wrap(`${i + 1}. ${st.title}  (+${st.recovers})`, 9.5, 'normal', [51, 65, 85], M + 4, W - 2 * M - 4));
    cur.y += 3;
  }
  if (data.profileLine || (data.pathToA && data.pathToA.length)) cur.y += 4;

  // Building-states note + tooltip-as-footnote (D2.3 / D2.4) — a static PDF
  // can't hover, so the tooltip "why" renders inline-small beneath the note.
  doc.setDrawColor(99, 102, 241);
  doc.setLineWidth(0.4);
  const noteTop = cur.y - 4;
  wrap(BUILDING_NOTE, 9.5, 'normal', [51, 65, 85], M + 4, W - 2 * M - 8);
  wrap(`(i) ${BUILDING_TOOLTIP}`, 8, 'italic', [100, 116, 139], M + 4, W - 2 * M - 8);
  doc.roundedRect(M, noteTop, W - 2 * M, cur.y - noteTop + 1, 2, 2);
  cur.y += 8;

  // Potential-gain + biggest-opportunity (reused logic from the owner report).
  const byCat: Record<string, number> = {};
  open.forEach((c) => { byCat[c.category] = (byCat[c.category] ?? 0) + 1; });
  const topCat = Object.entries(byCat).sort((a, b) => b[1] - a[1])[0];

  doc.setDrawColor(22, 163, 74);
  doc.setLineWidth(0.8);
  doc.roundedRect(M, cur.y, W - 2 * M, 20, 2.5, 2.5);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(22, 163, 74);
  doc.text(`+${gain} pts`, M + 6, cur.y + 9);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(71, 85, 105);
  doc.text(
    doc.splitTextToSize(
      `available if all ${open.length} open fixes are actioned (${n.high} high · ${n.medium} medium · ${n.low} low priority).`,
      W - 2 * M - 44,
    ),
    M + 40, cur.y + 7,
  );
  cur.y += 26;

  if (topCat) {
    doc.setDrawColor(99, 102, 241);
    doc.setLineWidth(0.8);
    doc.roundedRect(M, cur.y, W - 2 * M, 22, 2.5, 2.5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(67, 56, 202);
    doc.text(`Biggest opportunity: ${CAT_LABEL[topCat[0]] ?? topCat[0]}  (${topCat[1]} open fixes)`, M + 5, cur.y + 8);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    doc.text(doc.splitTextToSize(CAT_DESC[topCat[0]] ?? '', W - 2 * M - 10), M + 5, cur.y + 14);
    cur.y += 30;
  }

  // Open fixes by priority — All three labelled, zeros shown (UAT lone-bar fix).
  wrap('Open fixes by priority', 12, 'bold', [15, 23, 42], M);
  cur.y += 1;
  const barY = cur.y;
  const barW = W - 2 * M;
  let bx = M;
  (['high', 'medium', 'low'] as const).forEach((k) => {
    const v = n[k];
    if (!v || open.length === 0) return;
    const w = (barW * v) / open.length;
    doc.setFillColor(...PRI_COL[k]);
    doc.rect(bx, barY, w, 8, 'F');
    bx += w;
  });
  cur.y = barY + 13;
  doc.setFontSize(10);
  let lx = M;
  (['high', 'medium', 'low'] as const).forEach((k) => {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...PRI_COL[k]);
    const label = `${k[0].toUpperCase()}${k.slice(1)} ${n[k]}`;
    doc.text(label, lx, cur.y);
    lx += doc.getTextWidth(label) + 10;
  });
  cur.y += 9;

  // Footer provenance + appendix pointer.
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(148, 163, 184);
  doc.text(
    data.includeFixes
      ? `From your ${periodPhrase(data.reportMonthLabel)} Nexanomics assessment of ${data.hostname}. Full fix detail follows.`
      : `From your ${periodPhrase(data.reportMonthLabel)} Nexanomics assessment of ${data.hostname}.`,
    M, 290,
  );

  // Journeys are NO LONGER part of the Website report (ADR-096 Amendment 5,
  // PL3-5) — they are their own Trust report (section === 'trust' above). Mixing
  // the website summary and the journeys page in one PDF was the spec defect this
  // amendment corrects.

  // ── fix-card appendix (only when the toggle is on, D4) ──
  if (data.includeFixes && data.appendixCards.length > 0) {
    doc.addPage();
    cur.y = 20;
    wrap('Fix cards', 14, 'bold', [15, 23, 42], M);
    cur.y += 2;
    cardDetailPages(doc, cur, M, data.appendixCards, data.includeHow, wrap);
  }

  return doc;
}
